"use client";
import { saveDataAfterLogin, verifyOTPAction } from "@/actions/user.actions";
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  LoginPageEnums,
  LoginTypeEnum,
  SessionStorageKeysEnum,
  SUBSCRIPTION_STATUS,
  URLQueryParamsEnum,
  WindowEventListenersEnum,
} from "@/constants/constants";
import { jwtDecode } from "jwt-decode";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDialectFromClientCookies } from "@/hooks/useDialectFromClientCookies";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import * as LoginInterfaces from "@/types/login.types";
import * as UserTypes from "@/types/user.types";
import {
  getClientSideCookieValue,
  getHomePageRedirectUrlPostLogin,
  getMarketingAttributionFromSessionStorage,
  isTvEnv,
  postLoginEvents,
} from "@/utils/common.helpers";
import { handlePostLoginRedirection } from "./utils/login.helpers";
import { tvBackButtonOverrideHandler } from "@/utils/tv.helpers";
import { useRouter } from "next/navigation";
import { useActionState, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { OTPInput, OTPInputRef } from "./OTPInput";
import { ResendOTPButton } from "./ResendOTPButton";
import FullScreenLoader from "../FullScreenLoader";
import { useUserContext } from "@/context/UserProvider";

export const VerifyOTP = ({
  sendOTPState,
  sendOTPFormAction,
  resetLoginPageState,
  onInputFocus,
  onInputBlur,
}: {
  sendOTPState: UserTypes.SendOTPStateType;
  sendOTPFormAction: LoginInterfaces.SendOTPFormActionInterface;
  resetLoginPageState: () => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
}) => {
  const { t, currentLanguage } = useClientTranslations();
  const { trackEvent, identifyUser } = useTrackingAnalytics();
  const [verifyOTPState, verifyOTPFormAction] = useActionState(
    //@ts-ignore
    verifyOTPAction,
    undefined,
  );

  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const { login } = useUserContext();

  const [hasOTPBeenSent, setHasOTPBeenSent] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const [resendOTPCounter, setResendOTPCounter] = useState(0);
  const [clearVerifyOTPError, setClearVerifyOTPError] = useState(false);

  const otpInputRef = useRef<OTPInputRef>(null);

  const dialect = useDialectFromClientCookies();

  useEffect(() => {
    // stop truecaller login polling
    const event = new CustomEvent(
      WindowEventListenersEnum.STOP_TRUECALLER_LOGIN_POLLING,
    );
    window.dispatchEvent(event);
  }, []);

  const resendOTPTimerHandler = () => {
    setSeconds(30);

    let timerId: NodeJS.Timeout = setInterval(() => {
      setSeconds((seconds) => {
        if (seconds > 1) return seconds - 1;
        clearInterval(timerId);
        return 0;
      });
    }, 1000);

    return timerId;
  };

  useEffect(() => {
    toast.dismiss();
  }, []);

  useEffect(() => {
    const timerId = resendOTPTimerHandler();

    return () => clearInterval(timerId);
  }, [resendOTPCounter]);

  useEffect(() => {
    if (hasOTPBeenSent) {
      setClearVerifyOTPError(true);

      otpInputRef.current?.clearInputs();
    }
  }, [hasOTPBeenSent]);

  useEffect(() => {
    if (verifyOTPState?.errorMessage) {
      setClearVerifyOTPError(false);
    }
  }, [verifyOTPState]);

  const handleOTPInputChange = () => {
    setClearVerifyOTPError(true);
  };

  useEffect(() => {
    if (verifyOTPState?.userDetail?._id) {
      const urlParams = new URLSearchParams(window.location.search);
      const redirectionAfterLoginTo = urlParams.get("redirectionAfterLoginTo");

      const sourceSplit = redirectionAfterLoginTo?.split("/") ?? [];
      const sourcePage =
        sourceSplit.length > 0 ? sourceSplit[sourceSplit.length - 1] : "";

      setLoading(true);
      (async () => {
        await postLoginEvents(
          verifyOTPState?.userDetail,
          LoginTypeEnum.PHONE_NUMBER,
          sourcePage,
          trackEvent,
          identifyUser,
        );

        const homePageDialect =
          dialect ||
          (await getHomePageRedirectUrlPostLogin(
            verifyOTPState.userDetail.primaryLanguage,
          ));

        const redirectionUrlPostLoginSessionStorage = sessionStorage.getItem(
          SessionStorageKeysEnum.REDIRECTION_URL_POST_LOGIN,
        );

        const tvCode = urlParams.get(URLQueryParamsEnum.TV_CODE);
        const homePageURL = `/${currentLanguage}/${homePageDialect}${tvCode ? `?${URLQueryParamsEnum.TV_CODE}=${tvCode}` : ""}`;

        // Check if user came from free watch flow
        const freeWatchFlowCookie = getClientSideCookieValue(
          cookiesValues[CookiesEnum.FreeWatchFlow],
        );
        const isFromFreeWatchFlow = !!freeWatchFlowCookie;
        const isNonSubscribed =
          verifyOTPState.userDetail.subscriptionStatus !==
          SUBSCRIPTION_STATUS.SUBSCRIBED;

        // Fetch UTM parameters and deeplink from session storage
        const { utmDetails, deeplink } =
          getMarketingAttributionFromSessionStorage();
        const accessToken: string = verifyOTPState.accessToken;

        // Redirect non-subscribers from free watch flow to paywall (web only)
        if (isNonSubscribed && isFromFreeWatchFlow && !isTvEnv()) {
          // Save login data first
          await saveDataAfterLogin({
            accessToken,
            userDetails: verifyOTPState.userDetail,
            ...(utmDetails && { utmDetails }),
          });

          // Redirect to paywall with return URL
          const returnUrl = encodeURIComponent(window.location.pathname);
          window.location.href = `/paywall?returnTo=${returnUrl}`;
          return; // Exit early, don't continue with normal flow
        }

        // Handle Tambola flow: redirect to returnUrl with userId
        const isTambolaFlow =
          urlParams.get(URLQueryParamsEnum.IS_TAMBOLA_FLOW) === "true";
        const tambolaReturnUrl = urlParams.get(URLQueryParamsEnum.RETURN_URL);

        if (isTambolaFlow && tambolaReturnUrl) {
          await saveDataAfterLogin({
            accessToken,
            userDetails: verifyOTPState.userDetail,
            ...(utmDetails && { utmDetails }),
            ...(deeplink && { deeplink }),
          });

          // Use userId from user detail (same as JWT userId), with JWT decode as fallback
          let tambolaUserId = verifyOTPState.userDetail._id;
          try {
            const decoded = jwtDecode<{ userId: string }>(accessToken);
            if (decoded.userId) {
              tambolaUserId = decoded.userId;
            }
          } catch (e) {
            console.error("Error decoding token for Tambola flow:", e);
          }

          if (tambolaUserId) {
            const separator = tambolaReturnUrl.includes("?") ? "&" : "?";
            window.location.href = `${tambolaReturnUrl}${separator}userId=${tambolaUserId}`;
            return;
          }
        }

        const isNonSubsOrTrialTvUser = isNonSubscribed && isTvEnv();

        const redirectionUrl =
          redirectionAfterLoginTo ??
          redirectionUrlPostLoginSessionStorage ??
          homePageURL;

        const finalRedirectionUrl = isNonSubsOrTrialTvUser
          ? `/checkout?${URLQueryParamsEnum.DIRECLTY_FROM_LOGIN}=true`
          : redirectionUrl;

        await saveDataAfterLogin({
          accessToken,
          userDetails: verifyOTPState.userDetail,
          ...(utmDetails && { utmDetails }),
          ...(deeplink && { deeplink }),
        });

        // Handle post-login redirection
        const redirectUrl = await handlePostLoginRedirection(
          encodeURIComponent(btoa(finalRedirectionUrl)),
          login,
        );

        const tempCulture = getClientSideCookieValue(
          cookiesValues[CookiesEnum.TempCulture],
        );

        const userCulture = getClientSideCookieValue(
          cookiesValues[CookiesEnum.UserCulture],
        );

        if (redirectUrl) {
          if (tempCulture || userCulture) {
            window.location.href = redirectUrl;
          } else {
            window.location.href = `${AppRoutesEnum.SELECT_CULTURE}?redirectionAfterLoginTo=${encodeURIComponent(
              btoa(finalRedirectionUrl),
            )}`;
          }
        } else {
          setLoading(false);
        }
      })();
    }
  }, [verifyOTPState]);

  useEffect(() => {
    tvBackButtonOverrideHandler.set(() => {
      resetLoginPageState();
    });
    return () => {
      tvBackButtonOverrideHandler.clear();
    };
  }, []);

  return (
    <>
      <div>
        <form
          action={verifyOTPFormAction}
          className="relative flex-col items-center justify-center"
        >
          <div className="mx-auto rounded px-8 pt-2 text-center align-middle">
            <h2 className="mb-2 text-2xl font-medium text-white">
              {t("auth_screen.otpTitle")}
            </h2>
            <p className="text-sm font-normal text-stg-grey-4">
              {t("auth_screen.verify_otp_sent_on", [
                sendOTPState?.mobileNumber || "",
              ])}
            </p>
          </div>
          <div className="p-4">
            <div className="flex w-[100%] items-center justify-center rounded-lg">
              <label htmlFor="otp-digit-1" className="hidden">
                {LoginPageEnums.OTP}
              </label>
              <OTPInput
                ref={otpInputRef}
                onInputChange={handleOTPInputChange}
                onInputFocus={onInputFocus}
                onInputBlur={onInputBlur}
              />
            </div>
          </div>

          <input type="hidden" id="id" name="id" value={sendOTPState?.userId} />
          <input
            type="hidden"
            id="mobileNumber"
            name="mobileNumber"
            value={sendOTPState?.mobileNumber}
          />

          {verifyOTPState?.errorMessage && !clearVerifyOTPError && (
            <div className="mx-auto my-2 rounded px-8 text-center align-middle text-sm text-stg-red-1">
              {t("auth_screen.incorrectOtpMessage")}
            </div>
          )}
        </form>
        <form
          action={sendOTPFormAction}
          className="relative flex-col items-center justify-center"
        >
          <input
            type="hidden"
            id="mobileNumber"
            name="mobileNumber"
            value={sendOTPState?.mobileNumber}
          />
          <input
            type="hidden"
            id="isResendOtp"
            name="isResendOtp"
            value={"true"}
          />

          {seconds > 0 ? (
            <div
              className="mx-auto mb-6 mt-2 rounded px-8 text-center align-middle text-sm text-stg-grey-4"
              dangerouslySetInnerHTML={{
                __html: t("auth_screen.resent_otp_in", ["%seconds%"]).replace(
                  "%seconds%",
                  `<span class='text-stg-highlight'>${seconds}</span>`,
                ),
              }}
            ></div>
          ) : (
            <ResendOTPButton
              setHasOTPBeenSent={setHasOTPBeenSent}
              setResendOTPCounter={setResendOTPCounter}
            />
          )}
        </form>
      </div>
      {sendOTPState?.errorMessage && (
        <div className="mx-auto my-2 rounded-lg bg-stg-red-1-50 p-3 text-center align-middle text-xs font-normal text-white backdrop-blur-sm">
          {t("auth_screen.incorrectOtpMessage")}
        </div>
      )}
      {loading && <FullScreenLoader />}
    </>
  );
};
